/* eslint no-console: 0 */
import { hydrate, render } from 'react-dom';
import match from 'react-router/lib/match';
import browserHistory from 'react-router/lib/browserHistory';
import { loadableReady } from '@loadable/component';
import { createClientSideStore } from './store/configureStore';
import { trackTimeToFirstByte } from './utils/analytics';
import {
  configureBrowserSentry,
  captureException
} from './utils/browserSentry';
import { setExperimentCookies } from './utils/experiments';
import initializeI18n from './initializers/i18n/client';
import getContainerElement from './initializers/get_container_element';
import thirdPartyHacks from './initializers/third_party_hacks';
import polyfillClient from './initializers/polyfillClient';
import routes from './routes';

const CONTAINER_ID = 'r29-app';

/*
 * Sentry should be initialized before all other application code.
 */
configureBrowserSentry();

if (process.env.NODE_ENV !== 'production') {
  import(/* webpackChunkName: "featureTestHelpers" */ './initializers/featureTestHelpers')
    .then(featureTestHelpers => {
      featureTestHelpers.default(window);
    });
}

thirdPartyHacks(window);

polyfillClient()
  .then(() => {
    trackTimeToFirstByte(window);
    return createClientSideStore(window, document);
  })
  .then((store) => {
    return Promise.all([
      Promise.resolve(store),
      initializeI18n(store)
    ]);
  })
  .then(([store]) => {
    /*
     * Some components use location.protocol in their rendering logic but
     * React Router does not provide it in its `location` object on the server.
     *
     * We have to carefully ensure that location.hostname and location.protocol
     * are the same on the client and the server by parsing this data from the
     * server-side rendered DOM in '#r29-location-isomorphic-props'.
     *
     * In `gulp dev` there is no server-side rendered DOM, so we can use
     * window's data without worrying about conflicting data.
     */
    const locationIsomorphicPropsElement = document.getElementById(
      'r29-location-isomorphic-props'
    );
    const locationIsomorphicProps = locationIsomorphicPropsElement
      ? JSON.parse(locationIsomorphicPropsElement.innerHTML)
      : { protocol: window.location.protocol, hostname: window.location.host };
    const matchOptions = {
      history: browserHistory,
      routes: routes(store, locationIsomorphicProps)
    };
    match(matchOptions, (matchError, redirectLocation, renderProps) => {
      matchError && captureException(matchError);
      setExperimentCookies(store);
      try {
        const containerElement = getContainerElement(store, {
          clientSideRouterProps: renderProps
        });
        /*
         * If the document contains the #r29-app container then we've been
         * passed a server-side response with a container to hydrate.
         *
         * If the document does not contain the #r29-app container already
         * then we're in gulp dev and need to create a container to render into.
         */
        let container = document.getElementById(CONTAINER_ID);
        const givenServerSideResponse = !!container;
        if (!givenServerSideResponse) {
          container = document.createElement('div');
          container.id = CONTAINER_ID;
          document.body.appendChild(container);

          const basename = store.getState().i18n.edition_data.basename;
          const editionTagID = (basename.slice(0, basename.indexOf('-')) + (basename.slice(basename.indexOf('-') + 1)).toString().toUpperCase()) || ''; // Language and local if available
          const editionTagClass = basename.slice(0, basename.indexOf('-')) || ''; // Language only

          document.body.setAttribute('id', `${editionTagID}`);
          document.body.setAttribute('class', `${editionTagClass}`);
        }
        givenServerSideResponse
          ? loadableReady(() => hydrate(containerElement, container))
          : render(containerElement, container);
      } catch (renderError) {
        captureException(renderError);
      }
    });
  })
  .catch(captureException);
